import { Component, TemplateFunction } from '@ribajs/core'
import type {
  SbCartPresent,
  ShopifyProduct,
  ShopifyCartObject,
} from '../types/index'
import type { SlideshowSlide } from '../modules/tailwind/types/index.js'
import { hasChildNodesTrim } from '@ribajs/utils'
import { TwSlideshowComponent } from '../modules/tailwind/components/index.js'
import { ShopifyCartService } from '../services/index'
import { SbPopupComponent } from './sb-popup.component.js'
import { POPUP_PRESENTS_ID } from '../constants.js'

interface SbCartPresentsSlideshowSlideButton {
  type: 'button'
}

interface SbCartPresentsSlideshowSlideProduct {
  type: 'product'
  product: ShopifyProduct
  allowedVariantIds: number[]
}

interface Scope {
  presents: SbCartPresent[]
  slides: SlideshowSlide<
    SbCartPresentsSlideshowSlideProduct | SbCartPresentsSlideshowSlideButton
  >[]

  // METHODS
  hidePopup: SbCartPresentsSlideshowComponent['hidePopup']
  showPopup: SbCartPresentsSlideshowComponent['showPopup']
  togglePopup: SbCartPresentsSlideshowComponent['togglePopup']
}

/**
 * A component to contain and transform the `presents` to slides for the slideshow components.
 *
 * Set the `presents` with the `setPresents` method.
 */
export class SbCartPresentsSlideshowComponent extends Component {
  public static tagName = 'sb-cart-presents-slideshow'

  public scope: Scope = {
    presents: [],
    slides: [],

    // METHODS
    hidePopup: this.hidePopup.bind(this),
    showPopup: this.showPopup.bind(this),
    togglePopup: this.togglePopup.bind(this),
  }

  public slideshow?: TwSlideshowComponent

  protected cart?: ShopifyCartObject

  static get observedAttributes(): string[] {
    return ['presents']
  }

  protected requiredAttributes(): string[] {
    return []
  }

  protected connectedCallback() {
    super.connectedCallback()
    this.init(SbCartPresentsSlideshowComponent.observedAttributes)
    this.addEventListeners()
  }

  async afterBind() {
    await super.afterBind()

    this.cart = await ShopifyCartService.get()
    this.slideshow =
      this.querySelector(TwSlideshowComponent.tagName) || undefined

    this.classList.add(`${SbCartPresentsSlideshowComponent.tagName}-ready`)
  }

  protected getPopup() {
    const popup =
      (document.getElementById(POPUP_PRESENTS_ID) as SbPopupComponent | null) ||
      undefined
    if (!popup) {
      throw new Error(
        `sb-popup element with id "${POPUP_PRESENTS_ID}" not found!`,
      )
    }
    return popup
  }

  public hidePopup() {
    const popup = this.getPopup()
    popup.hide()
  }

  public showPopup() {
    const popup = this.getPopup()
    popup.show()
  }

  public togglePopup() {
    const popup = this.getPopup()
    popup.toggle()
  }

  protected presentsToSlides() {
    let index = 0
    const slides = new Array<
      SlideshowSlide<
        SbCartPresentsSlideshowSlideProduct | SbCartPresentsSlideshowSlideButton
      >
    >()
    for (const present of this.scope.presents) {
      if (!present) {
        console.error('present is null!')
        continue
      }
      if (!present.products) {
        console.error('present.products is undefined!')
        continue
      }

      if (!present.visible) {
        continue
      }

      for (let i = 0; i < present.products.length; i++) {
        const product = present.products[i]
        const allowedVariantIds = present.productVariantIds.filter(variantId =>
          product.variants.find(variant => variant.id === variantId),
        )
        const data: SbCartPresentsSlideshowSlideProduct = {
          type: 'product',
          product,
          allowedVariantIds,
        }
        slides.push({
          active: false,
          index,
          data,
        })
        index++
      }
    }

    const data: SbCartPresentsSlideshowSlideButton = {
      type: 'button',
    }
    // The last slide is a button
    slides.push({
      active: false,
      index: slides.length,
      data,
    })

    return slides
  }

  /**
   * This method is called by the `sb-cart-presents-notification` component to set the presents which are passed to the `sb-cart-presents-slideshow` components
   */
  public setPresents(presents: SbCartPresent[]) {
    if (this.scope.presents !== presents) {
      this.scope.presents = presents
    }

    this.scope.slides = this.presentsToSlides()
    this.updateSlides()
  }

  /**
   * Called as soon as this element is removed from the DOM
   */
  protected disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListeners()
  }

  protected updateSlides() {
    this.slideshow?.updateItems()
  }

  protected addEventListeners() {
    //
  }

  protected removeEventListeners() {
    //
  }

  protected template(): ReturnType<TemplateFunction> {
    // Use this template if this custom element has no child html
    if (!hasChildNodesTrim(this)) {
      return (
        <p class="error">
          This component has no it's own template, you need to add your own
          child HTML
        </p>
      )
    }
    return null
  }
}
